import React, { useEffect, useState, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { supabase } from './lib/supabase';
import { initPushNotifications, requestNotificationPermission } from './lib/pushNotifications';
import Home from './pages/Home';
import Dashboard from './pages/Dashboard';
import Profile from './pages/Profile';
import Pricing from './pages/Pricing';
import Login from './components/Login';
import Signup from './components/Signup';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';

// AudioContext unique — créé une seule fois au premier clic
let globalAudioCtx = null;

function unlockAudio() {
  if (!globalAudioCtx) {
    try {
      globalAudioCtx = new (window.AudioContext || window.webkitAudioContext)();
      globalAudioCtx.resume();
    } catch(e) {}
  } else if (globalAudioCtx.state === 'suspended') {
    globalAudioCtx.resume();
  }
}

function playNotif() {
  try {
    if (!globalAudioCtx) return;
    if (globalAudioCtx.state === 'suspended') globalAudioCtx.resume();
    const o = globalAudioCtx.createOscillator();
    const g = globalAudioCtx.createGain();
    o.connect(g);
    g.connect(globalAudioCtx.destination);
    o.type = 'sine';
    o.frequency.setValueAtTime(900, globalAudioCtx.currentTime);
    o.frequency.exponentialRampToValueAtTime(1200, globalAudioCtx.currentTime + 0.12);
    g.gain.setValueAtTime(0.35, globalAudioCtx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.001, globalAudioCtx.currentTime + 0.45);
    o.start(globalAudioCtx.currentTime);
    o.stop(globalAudioCtx.currentTime + 0.45);
  } catch(e) {
    console.log('Sound error:', e);
  }
}

// AFRICA_BLOCK
const AFRICA_COUNTRIES = new Set(['MA','DZ','TN','LY','EG','SD','ET','NG','KE','ZA','GH','CM','SN','CI','MG','AO','MZ','TZ','UG','ZW','ZM','BF','ML','NE','TD','SO','ER','RW','BI','MW','NA','BW','LS','SZ','GM','GN','GW','SL','LR','TG','BJ','GA','CG','CD','CF','SS','DJ','KM','MU','SC','CV','ST']);

function AfricaBlock() {

  return (
    <div style={{minHeight:'100vh',background:'#080808',display:'flex',alignItems:'center',justifyContent:'center',padding:'20px'}}>
      <div style={{textAlign:'center',maxWidth:'320px'}}>
        <div style={{fontSize:'2rem',marginBottom:'16px'}}>🌍</div>
        <div style={{color:'white',fontWeight:700,fontSize:'1.2rem',marginBottom:'8px',letterSpacing:'-0.5px'}}>Coming soon to your region</div>
        <div style={{color:'rgba(255,255,255,0.35)',fontSize:'0.88rem',lineHeight:1.7}}>nearme is not available in your country yet. We're expanding soon — stay tuned.</div>
        <div style={{marginTop:'24px',fontSize:'0.8rem',color:'rgba(255,255,255,0.15)'}}>nearmeapp.io</div>
      </div>
    </div>
  );
}

export default function App() {
  const [session, setSession] = useState(null);
  const [isBlocked, setIsBlocked] = useState(false);

  useEffect(() => {
    fetch('https://ipapi.co/json/')
      .then(r => r.json())
      .then(d => { if (AFRICA_COUNTRIES.has(d.country_code)) setIsBlocked(true); })
      .catch(() => {});
  }, []);
  const [loading, setLoading] = useState(true);
  const [unreadCounts, setUnreadCounts] = useState({});
  const [blockedIds, setBlockedIds] = useState(new Set());
  const channelRef = useRef(null);
  const blockedIdsRef = useRef(new Set());
  const chatUserIdRef = useRef(null);

  // Sync blockedIds to ref
  useEffect(() => { blockedIdsRef.current = blockedIds; }, [blockedIds]);

  // Init push notifications
  useEffect(() => {
    initPushNotifications().then(() => {
      // Request permission after first interaction
      document.addEventListener('click', () => requestNotificationPermission(), { once: true });
    });
  }, []);

  // Unlock audio on ANY click anywhere
  useEffect(() => {
    document.addEventListener('click', unlockAudio);
    document.addEventListener('touchstart', unlockAudio);
    return () => {
      document.removeEventListener('click', unlockAudio);
      document.removeEventListener('touchstart', unlockAudio);
    };
  }, []);

  // Auth
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setLoading(false);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, session) => {
      setSession(session);
    });
    return () => subscription.unsubscribe();
  }, []);

  // Global realtime listener — dans App.js, ne se démonte jamais
  useEffect(() => {
    if (!session?.user?.id) return;
    const myId = session.user.id;

    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    }

    const ch = supabase
      .channel(`app_notif_${myId}_${Date.now()}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'messages',
        filter: `receiver_id=eq.${myId}`,
      }, (payload) => {
        const senderId = payload.new.sender_id;
        if (blockedIdsRef.current.has(senderId)) return;

        // TOUJOURS jouer le son à la réception
        playNotif();

        // Badge si chat pas ouvert avec cet user
        if (chatUserIdRef.current !== senderId) {
          setUnreadCounts(prev => ({
            ...prev,
            [senderId]: (prev[senderId] || 0) + 1,
          }));
        }
      })
      .subscribe((status) => {
        console.log('Notif channel status:', status);
      });

    channelRef.current = ch;
    return () => {
      if (channelRef.current) supabase.removeChannel(channelRef.current);
    };
  }, [session?.user?.id]);

  const handleUnblock = (userId) => {
    setBlockedIds(prev => { const next = new Set(prev); next.delete(userId); return next; });
  };

  const handleBlock = (userId) => {
    setBlockedIds(prev => new Set([...prev, userId]));
    setUnreadCounts(prev => { const n = {...prev}; delete n[userId]; return n; });
  };

  const clearUnread = (userId) => {
    chatUserIdRef.current = userId;
    setUnreadCounts(prev => ({ ...prev, [userId]: 0 }));
  };

  const onChatClose = () => { chatUserIdRef.current = null; };

  if (loading) return (
    <div style={{ background:'#080808', height:'100vh', display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ width:'24px', height:'24px', border:'2px solid rgba(255,255,255,0.08)', borderTop:'2px solid white', borderRadius:'50%', animation:'spin 0.8s linear infinite' }}/>
    </div>
  );

  const totalUnread = Object.values(unreadCounts).reduce((a,b) => a+b, 0);

  return (
    <Router>
      <Routes>
        <Route path="/" element={!session ? <Home/> : <Navigate to="/dashboard"/>}/>
        <Route path="/login" element={!session ? <Login/> : <Navigate to="/dashboard"/>}/>
        <Route path="/signup" element={!session ? <Signup/> : <Navigate to="/dashboard"/>}/>
        <Route path="/pricing" element={<Pricing session={session}/>}/>
        <Route path="/terms" element={<Terms/>}/>
        <Route path="/privacy" element={<Privacy/>}/>
        <Route path="/dashboard" element={
          session
            ? <Dashboard
                session={session}
                unreadCounts={unreadCounts}
                setUnreadCounts={setUnreadCounts}
                clearUnread={clearUnread}
                onChatClose={onChatClose}
                blockedIds={blockedIds}
                onBlock={handleBlock}
                onUnblock={handleUnblock}
                totalUnread={totalUnread}
              />
            : <Navigate to="/login"/>
        }/>
        <Route path="/profile" element={session ? <Profile session={session}/> : <Navigate to="/login"/>}/>
        <Route path="*" element={<Navigate to="/"/>}/>
      </Routes>
    </Router>
  );
}
